import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Shield, Plus, Trash2, Mail, Phone, MapPin, X, ShieldAlert, CheckCircle2 } from "lucide-react";

export function AddGuardModal({ open, onClose, onSaved }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [gate, setGate] = useState("Main Gate");
    const [saving, setSaving] = useState(false);

    if (!open) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) {
            toast.error("Google email is required");
            return;
        }
        setSaving(true);
        try {
            const res = await api.post("/admin/guards", {
                name: name.trim() || undefined,
                email: email.trim().toLowerCase(),
                phone: phone.trim() || undefined,
                gate: gate.trim() || "Main Gate",
            });
            toast.success(`Guard "${res.data.name || email}" added successfully!`);
            onSaved();
            onClose();
            setName("");
            setEmail("");
            setPhone("");
            setGate("Main Gate");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not add guard");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4 backdrop-blur-sm animate-fade-in" onClick={onClose}>
            <div
                className="bg-white rounded-2xl border border-nest-line w-full max-w-md p-6 lg:p-8 shadow-2xl relative"
                onClick={(e) => e.stopPropagation()}
                data-testid="add-guard-modal"
            >
                <div className="flex items-center justify-between pb-4 border-b border-nest-line">
                    <div className="flex items-center gap-2.5">
                        <div className="h-9 w-9 rounded-xl bg-nest-ink text-white grid place-items-center">
                            <Shield size={18} />
                        </div>
                        <div>
                            <p className="nest-stat-label">Access Control</p>
                            <h3 className="font-display text-2xl tracking-tight">Add Security Guard</h3>
                        </div>
                    </div>
                    <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-nest-alt text-nest-ink3 hover:text-nest-ink">
                        <X size={18} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="mt-5 grid gap-4">
                    <div>
                        <label className="nest-stat-label">Guard Name / Designation</label>
                        <input
                            required
                            type="text"
                            placeholder="e.g., Ramesh Singh (or Main Gate Tablet)"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="mt-1.5 w-full bg-white border border-nest-line rounded-xl px-3.5 py-2.5 outline-none focus:border-nest-clay text-sm"
                            data-testid="guard-name-input"
                        />
                    </div>

                    <div>
                        <label className="nest-stat-label">Google Account Email <span className="text-nest-clay">*</span></label>
                        <input
                            required
                            type="email"
                            placeholder="guard.security@gmail.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="mt-1.5 w-full bg-white border border-nest-line rounded-xl px-3.5 py-2.5 outline-none focus:border-nest-clay text-sm font-mono text-xs"
                            data-testid="guard-email-input"
                        />
                        <p className="text-[11px] text-nest-ink3 mt-1">
                            The guard or gate device will use this Google email to log in.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="nest-stat-label">Phone (Optional)</label>
                            <input
                                type="tel"
                                placeholder="+91 98765 43210"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                className="mt-1.5 w-full bg-white border border-nest-line rounded-xl px-3.5 py-2.5 outline-none focus:border-nest-clay text-sm"
                                data-testid="guard-phone-input"
                            />
                        </div>
                        <div>
                            <label className="nest-stat-label">Assigned Gate</label>
                            <input
                                type="text"
                                placeholder="Main Gate"
                                value={gate}
                                onChange={(e) => setGate(e.target.value)}
                                className="mt-1.5 w-full bg-white border border-nest-line rounded-xl px-3.5 py-2.5 outline-none focus:border-nest-clay text-sm"
                                data-testid="guard-gate-input"
                            />
                        </div>
                    </div>

                    <div className="p-3 bg-nest-alt/60 rounded-xl border border-nest-line/80 flex items-start gap-2.5 text-xs text-nest-ink2">
                        <CheckCircle2 size={16} className="text-nest-sage flex-shrink-0 mt-0.5" />
                        <p>
                            Once added, when this Google account signs in, they will automatically land on the <strong>Gate Desk</strong> to scan visitor QR passes and log entries.
                        </p>
                    </div>

                    <div className="flex items-center justify-end gap-3 pt-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2.5 rounded-xl border border-nest-line text-nest-ink2 hover:bg-nest-alt text-sm font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="nest-btn-primary flex items-center gap-2"
                            data-testid="save-guard-btn"
                        >
                            {saving ? "Adding..." : "Add Guard"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function GuardsManager() {
    const [guards, setGuards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [q, setQ] = useState("");

    const loadGuards = async () => {
        setLoading(true);
        try {
            const res = await api.get("/admin/guards");
            setGuards(res.data);
        } catch (err) {
            toast.error("Could not load security guards");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadGuards();
    }, []);

    const handleRemoveGuard = async (guard) => {
        if (!window.confirm(`Are you sure you want to remove guard "${guard.name || guard.email}"? Their gate access will be revoked.`)) {
            return;
        }
        try {
            await api.delete(`/admin/guards/${guard.user_id}`);
            toast.success("Guard removed successfully");
            loadGuards();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not remove guard");
        }
    };

    const filtered = guards.filter((g) => {
        if (!q.trim()) return true;
        const s = q.toLowerCase();
        return [g.name, g.email, g.phone, g.gate].some((v) => (v || "").toLowerCase().includes(s));
    });

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-6">
                <div>
                    <h2 className="font-display text-2xl tracking-tight">Security Guard Roster</h2>
                    <p className="text-nest-ink2 text-sm mt-1">
                        Authorised gate staff and security personnel with Gate Desk access.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search guards..."
                        className="bg-white border border-nest-line rounded-lg px-3.5 py-2 w-full sm:w-56 outline-none focus:border-nest-clay text-sm"
                        data-testid="guard-search-input"
                    />
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="nest-btn-primary flex items-center gap-2 whitespace-nowrap"
                        data-testid="open-add-guard-btn"
                    >
                        <Plus size={16} /> Add Guard
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="py-12 text-center text-nest-ink2">Loading security personnel...</div>
            ) : filtered.length === 0 ? (
                <div className="nest-card p-10 text-center text-nest-ink2">
                    <div className="h-12 w-12 rounded-2xl bg-nest-alt mx-auto grid place-items-center text-nest-ink3 mb-3">
                        <ShieldAlert size={24} />
                    </div>
                    <p className="font-display text-lg text-nest-ink">No guards found</p>
                    <p className="text-sm mt-1 max-w-md mx-auto">
                        Add security guard email addresses so they can sign in and operate the Gate Desk scanner.
                    </p>
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="nest-btn-primary inline-flex items-center gap-2 mt-4"
                    >
                        <Plus size={16} /> Add Guard Account
                    </button>
                </div>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {filtered.map((g) => (
                        <div key={g.user_id} className="nest-card p-5 flex flex-col justify-between" data-testid={`guard-card-${g.user_id}`}>
                            <div>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                        {g.picture ? (
                                            <img src={g.picture} alt="" className="h-12 w-12 rounded-full object-cover border border-nest-line" />
                                        ) : (
                                            <div className="h-12 w-12 rounded-xl bg-nest-ink text-white grid place-items-center font-display font-semibold text-lg">
                                                <Shield size={22} />
                                            </div>
                                        )}
                                        <div>
                                            <p className="font-semibold text-base text-nest-ink tracking-tight">{g.name || "Security Guard"}</p>
                                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200 mt-0.5">
                                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse"></span> Active Guard
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleRemoveGuard(g)}
                                        className="text-nest-ink3 hover:text-nest-err p-1.5 rounded-lg hover:bg-red-50 transition"
                                        title="Revoke guard access"
                                        data-testid={`remove-guard-${g.user_id}`}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>

                                <div className="mt-4 pt-3 border-t border-nest-line/80 space-y-1.5 text-xs text-nest-ink2">
                                    <div className="flex items-center gap-2 font-mono text-[11px] truncate">
                                        <Mail size={13} className="text-nest-ink3 flex-shrink-0" />
                                        <span className="truncate">{g.email}</span>
                                    </div>
                                    {g.phone && (
                                        <div className="flex items-center gap-2">
                                            <Phone size={13} className="text-nest-ink3 flex-shrink-0" />
                                            <span>{g.phone}</span>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <MapPin size={13} className="text-nest-clay flex-shrink-0" />
                                        <span className="font-medium text-nest-ink">{g.gate || "Main Gate"}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <AddGuardModal open={showAddModal} onClose={() => setShowAddModal(false)} onSaved={loadGuards} />
        </div>
    );
}
